import React, { useEffect, useState } from "react";
import CustomBreadcrumbs from "../../components/BreadCrums";
import {
  Stack,
  Button,
  Container,
  FormControlLabel,
  Switch,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Paper,
  Box,
  Divider,
  IconButton,
  styled,
} from "@mui/material";
import { Copy, ShieldCheck, ShieldOff, Key, AlertTriangle } from "lucide-react";
import {
  setUpTOTP,
  updateMFAPreference,
  verifyTOTPSetup,
  fetchMFAPreference,
} from "aws-amplify/auth";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
}));

const StatusBadge = styled(Box)(({ theme, enabled }) => ({
  display: "inline-flex",
  alignItems: "center",
  padding: theme.spacing(0.5, 1.5),
  borderRadius: theme.spacing(2),
  backgroundColor: enabled
    ? "rgba(46, 125, 50, 0.1)"
    : "rgba(211, 47, 47, 0.1)",
  color: enabled ? "#2E7D32" : "#D32F2F",
  fontSize: "0.875rem",
  fontWeight: 500,
  marginLeft: theme.spacing(2),
}));

const breadcrumbItems = [
  { label: "Account settings", link: "#" },
  { label: "Security Settings" },
];

const SecuritySettings = () => {
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [showTOTPDialog, setShowTOTPDialog] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [setupUri, setSetupUri] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [error, setError] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [success, setSuccess] = useState("");

  const handle2FAChange = async (event) => {
    if (is2FAEnabled) {
      // Show disable confirmation dialog
      setShowDisableDialog(true);
    } else {
      try {
        const totpSetupDetails = await setUpTOTP();
        const appName = "DreamStreamIDP";
        const uri = totpSetupDetails.getSetupUri(appName);
        console.log(uri);
        const secret = uri.searchParams.get("secret");
        setSecretKey(secret);
        setSetupUri(uri);
        setShowTOTPDialog(true);
      } catch (err) {
        setError("Failed to setup 2FA. Please try again.");
        console.error(err);
      }
    }
  };

  const handleDisable2FA = async () => {
    try {
      const disableResponse = await updateMFAPreference({ totp: "DISABLED" });
      console.log(disableResponse, "disableResponse");
      setIs2FAEnabled(false);
      setShowDisableDialog(false);
      setSuccess("2FA has been successfully disabled!");
    } catch (err) {
      setError("Failed to disable 2FA. Please try again.");
      console.error(err);
    }
  };

  function extractSecretFromTOTPUri(uri) {
    try {
      // Create a URL object from the URI
      // Replace 'totp' with 'https' temporarily to make it a valid URL
      const url = new URL(uri.replace("otpauth://totp/", "https://"));

      // Get the search parameters
      const searchParams = new URLSearchParams(url.search);

      // Get the secret from the parameters
      const secret = searchParams.get("secret");

      if (!secret) {
        throw new Error("Secret not found in URI");
      }

      return secret;
    } catch (error) {
      console.error("Failed to parse TOTP URI:", error);
      return null;
    }
  }

  const handleCopyUri = async () => {
    try {
      await navigator.clipboard.writeText(setupUri);
      setSuccess("URI copied to clipboard!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError("Failed to copy URI. Please try manually.");
      setTimeout(() => setError(""), 3000);
    }
  };

  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(secretKey);
      setSuccess("Secret key copied to clipboard!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError("Failed to copy secret key. Please try manually.");
      setTimeout(() => setError(""), 3000);
    }
  };

  const handleVerifyAndEnable = async () => {
    try {
      if (!verificationCode) {
        setError("Please enter verification code");
        return;
      }

      const verifyTOTPResponse = await verifyTOTPSetup({
        code: verificationCode,
      });
      console.log(verifyTOTPResponse, "verifyTOTPResponse");
      await updateMFAPreference({ totp: "ENABLED" });

      setIs2FAEnabled(true);
      setShowTOTPDialog(false);
      setSuccess("2FA has been successfully enabled!");
      setVerificationCode("");
      setError("");
    } catch (err) {
      setError("Invalid verification code. Please try again.");
      console.error(err);
    }
  };

  const handleClose = () => {
    setShowTOTPDialog(false);
    setShowDisableDialog(false);
    setVerificationCode("");
    setError("");
    setSecretKey("");
  };

  useEffect(() => {
    const fetchMFASettings = async () => {
      try {
        const { enabled, preferred } = await fetchMFAPreference();
        if (enabled && enabled.length > 0) {
          setIs2FAEnabled(true);
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchMFASettings();
  }, []);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <CustomBreadcrumbs items={breadcrumbItems} />

      {success && (
        <Alert
          severity="success"
          sx={{
            width: "100%",
            mb: 3,
            borderRadius: 2,
          }}
        >
          {success}
        </Alert>
      )}

      <StyledPaper sx={{ marginY: 3 }}>
        <Stack spacing={3}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            {is2FAEnabled ? (
              <ShieldCheck size={24} color="#2E7D32" />
            ) : (
              <ShieldOff size={24} color="#D32F2F" />
            )}
            <Typography variant="h6" sx={{ ml: 2 }}>
              Two-Factor Authentication (2FA)
              <StatusBadge enabled={is2FAEnabled}>
                {is2FAEnabled ? "Enabled" : "Disabled"}
              </StatusBadge>
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Two-factor authentication adds an extra layer of security to your
            account by requiring both your password and a verification code from
            your authenticator app.
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={is2FAEnabled}
                onChange={handle2FAChange}
                color="primary"
              />
            }
            label={
              <Typography variant="body1">
                {is2FAEnabled ? "Disable" : "Enable"} Two-Factor Authentication
              </Typography>
            }
          />
        </Stack>
      </StyledPaper>

      {/* Enable 2FA Dialog */}
      <Dialog
        open={showTOTPDialog}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            pb: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Key size={24} />
            <Typography variant="h6">
              Setup Two-Factor Authentication
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Stack spacing={4}>
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight="500">
                1. Setup Your Authenticator App
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Use either the URI or secret key to configure your authenticator
                app:
              </Typography>

              <Stack spacing={3} sx={{ mt: 2 }}>
                <Box sx={{ bgcolor: "grey.50", p: 2, borderRadius: 1 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Option A: Full URI
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                      fullWidth
                      value={setupUri}
                      InputProps={{ readOnly: true }}
                      size="small"
                    />
                    <Button
                      variant="outlined"
                      onClick={handleCopyUri}
                      startIcon={<Copy size={16} />}
                    >
                      Copy
                    </Button>
                  </Stack>
                </Box>

                <Box sx={{ bgcolor: "grey.50", p: 2, borderRadius: 1 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Option B: Secret Key
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                      fullWidth
                      value={secretKey}
                      InputProps={{ readOnly: true }}
                      size="small"
                    />
                    <Button
                      variant="outlined"
                      onClick={handleCopySecret}
                      startIcon={<Copy size={16} />}
                    >
                      Copy
                    </Button>
                  </Stack>
                </Box>
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight="500">
                2. Verify Setup
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Enter the 6-digit code from your authenticator app to complete
                setup:
              </Typography>
              <TextField
                fullWidth
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter verification code"
                error={!!error}
                helperText={error}
                size="small"
                sx={{ mt: 1 }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}
        >
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleVerifyAndEnable}
            variant="contained"
            startIcon={<ShieldCheck size={16} />}
          >
            Enable 2FA
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog
        open={showDisableDialog}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            pb: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <AlertTriangle size={24} color="#D32F2F" />
            <Typography variant="h6" color="error">
              Disable Two-Factor Authentication
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Disabling 2FA will make your account more vulnerable to unauthorized
            access.
          </Alert>
          <Typography variant="body1">
            Are you sure you want to disable Two-Factor Authentication?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}
        >
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDisable2FA}
            variant="contained"
            color="error"
            startIcon={<ShieldOff size={16} />}
          >
            Disable 2FA
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SecuritySettings;
